import React, { useEffect, useState } from 'react'
import { getFormContainer } from '../../container/form-module'
import { IUserFormService, USER_FORM_SERVICE_KEY } from '../../modules/forms'
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import style from './AssignTo.module.css'
import { BoolQueryParam, Query, QueryParam } from '../../common/api/Query'
import { UserQuery } from '../../modules/users/models/User'
import { getUserContainer } from '../../container/user-module'
import { IUserService, USER_SERVICE_KEY } from '../../modules/users'
import { useTranslation } from 'react-i18next'
import { Header } from '../../components/header/Header'
import { CronItemMockup } from '../../components/form-card/CronItemMockup'
import { Cron, cronString } from '../../modules/forms/enums/Cron'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { emptyUserFormDTO, toModel } from '../../modules/forms/models/UserFormDTO'
import { useSnackbar } from 'notistack'
import close from '../../assets/table_icons/delete.svg'
import { forkJoin } from 'rxjs'

type Participant = {
  id: string
  name: string
}

type AssignToProps = {
  open: boolean
  formID: string
  onClose: () => void
}

const formContainer = getFormContainer()
const userFormService = formContainer.get<IUserFormService>(USER_FORM_SERVICE_KEY)
const userService = getUserContainer().get<IUserService>(USER_SERVICE_KEY)

// Fuera del componente: cronString() devuelve un objeto nuevo en cada render y
// dentro obligaria a meterlo como dependencia de los efectos.
const CRON_TYPES = cronString()

export const AssignTo = ({ open, formID, onClose }: AssignToProps): JSX.Element | null => {
  const { t } = useTranslation()
  const [participants, setParticipants] = useState<Participant[]>([])
  const [selectedUserIDs, setSelectedUserIDs] = useState<string[]>([])
  const [isRecurrence, setIsRecurrence] = useState<boolean>(false)
  const [cron, setCron] = useState<string>(CRON_TYPES[Cron.Empty])
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [finishDate, setFinishDate] = useState<Date>(new Date())
  const [isAssigning, setIsAssigning] = useState<boolean>(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleChangeRecurrence = (s: string, start: Date, finish: Date) => {
    setCron(s)
    setStartDate(start)
    setFinishDate(finish)
  }

  // Cada apertura arranca limpia: si no, el modal recuerda a quien se asigno la
  // vez anterior.
  useEffect(() => {
    if (!open) return
    setSelectedUserIDs([])
    setIsRecurrence(false)
    setCron(CRON_TYPES[Cron.Empty])
    setStartDate(new Date())
    setFinishDate(new Date())
  }, [open])

  useEffect(() => {
    if (!open) return
    userService
      .getFilteredList(
        new Query({
          query: [new QueryParam<UserQuery>('isPatient', new BoolQueryParam(true))],
        })
      )
      .subscribe((res) =>
        setParticipants(
          res.items.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}`.trim() }))
        )
      )
  }, [open])

  const saveAssignation = () => {
    if (isAssigning) return
    if (!selectedUserIDs.length) {
      enqueueSnackbar(t('mustSelectAtLeastOneUser'), { variant: 'error' })
      return
    }

    setIsAssigning(true)

    // Una asignacion por usuario seleccionado: el endpoint recibe un unico
    // paciente por peticion.
    forkJoin(
      selectedUserIDs.map((userID) => {
        const userForm = emptyUserFormDTO(userID, '')
        userForm.formID = formID
        return userFormService.add(
          toModel(userForm),
          isRecurrence ? cron : CRON_TYPES[Cron.Empty],
          isRecurrence ? startDate : new Date(),
          isRecurrence ? finishDate : new Date()
        )
      })
    ).subscribe({
      next: (results) => {
        setIsAssigning(false)
        // UserFormApi captura el error, avisa por su cuenta y devuelve
        // undefined, asi que la rama de error de aqui no llega a dispararse.
        // Hay que mirar el resultado o se canta exito sobre una asignacion que
        // no ha entrado, que es justo lo que pasaba antes.
        if (results.some((r) => !r)) return

        enqueueSnackbar(t('assignedFormSuccessfully'), { variant: 'success' })
        onClose()
      },
      error: () => {
        setIsAssigning(false)
        enqueueSnackbar(t('assignmentError'), { variant: 'error' })
      },
    })
  }

  if (!open) return null

  return (
    <div className={style.overlay}>
      <div className={style.containerInside}>
        <Header label={t('assignQuestionnaire')} icon={close} onClick={onClose} />

        <p className={style.subtitle}>{t('selectUsersToSend')}</p>

        <Box className={style.fieldRow}>
          <p className={style.label}>{t('user')}</p>
          <FormControl size="small" className={style.userSelect}>
            <Select
              multiple
              displayEmpty
              value={selectedUserIDs}
              onChange={(e) => setSelectedUserIDs(e.target.value as string[])}
              renderValue={(selected) => {
                const ids = selected as string[]
                if (!ids.length) {
                  return <span className={style.placeholder}>{t('selectUser')}</span>
                }
                return participants
                  .filter((p) => ids.includes(p.id))
                  .map((p) => p.name)
                  .join(', ')
              }}
            >
              {participants.length === 0 && (
                <MenuItem disabled value="">
                  {t('noElementsAvailable')}
                </MenuItem>
              )}
              {participants.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Checkbox checked={selectedUserIDs.includes(p.id)} size="small" />
                  <ListItemText primary={p.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={style.recurrenceRow}>
          <Typography className={style.label}>{t('recurrence')}</Typography>
          <Checkbox
            checked={isRecurrence}
            name={'recurrence'}
            onChange={(e) => setIsRecurrence(e.target.checked)}
            sx={{ padding: 0 }}
          />
        </Box>

        {isRecurrence && (
          <Box className={style.recurrenceBlock}>
            <CronItemMockup handleChange={handleChangeRecurrence} />
          </Box>
        )}

        <Box className={style.footer}>
          <AppButton
            theme={ButtonTheme.NewSecondary}
            type={'button'}
            label={t('cancel')}
            handler={onClose}
          />
          <AppButton
            theme={ButtonTheme.NewPrimary}
            type={'button'}
            label={t('assign')}
            handler={saveAssignation}
          />
        </Box>
      </div>
    </div>
  )
}
